import { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Button,
  message,
  Space,
  Row,
  Col,
  Typography
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';

import { News, NewsCategory, NewsStatus, NewsReference } from '@/types/community/news';
import { addNews, updateNews } from '@/api/news';
import { useAuth } from '@/contexts/AuthContext';
import { AlertSelector } from '@/components/common/AlertSelector';
import { EventSelector } from '@/components/common/EventSelector';
import { GuidelineSelector } from '@/components/common/GuidelineSelector';
import { IncidentSelector } from '@/components/common/IncidentSelector';
import { LostAndFoundSelector } from '@/components/common/LostAndFoundSelector';
import type { FileMetadata } from '@/types/common';

const { TextArea } = Input;
const { Title } = Typography;

interface NewsModalProps {
  open: boolean;
  news?: News | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const NEWS_CATEGORIES: { value: NewsCategory; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'alert', label: 'Alert' },
  { value: 'event', label: 'Event' },
  { value: 'guideline', label: 'Guideline' },
  { value: 'incident', label: 'Incident' },
  { value: 'lostAndFound', label: 'Lost & Found' }
];

const NEWS_STATUSES: { value: NewsStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' }
];

export default function NewsModal({ open, news, onCancel }: NewsModalProps) {
  const { t } = useTranslation(['news', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [photoList, setPhotoList] = useState<UploadFile[]>([]);

  const [selectedCategory, setSelectedCategory] = useState<NewsCategory>('general');

  // Mutations
  const addNewsMutation = useMutation({
    mutationFn: (data: Omit<News, 'id'>) => addNews(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('news:addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['news'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Add news error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:errorOccurred');
      message.error(`Failed to add news: ${errorMessage}`);
    }
  });

  const updateNewsMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Omit<News, 'id'>> }) => 
      updateNews(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('news:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['news'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Update news error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:errorOccurred');
      message.error(`Failed to update news: ${errorMessage}`);
    }
  });

  // Initialize form when news changes
  useEffect(() => {
    if (news) {
      form.setFieldsValue({
        title: news.title,
        description: news.description,
        category: news.category,
        status: news.status,
        referenceId: news.reference?.id,
        expiryDate: news.expiryDate ? dayjs(news.expiryDate) : undefined
      });

      // Set related data
      setSelectedCategory(news.category);

      // Set file list
      if (news.attachments) {
        const files: UploadFile[] = news.attachments.map((attachment, index) => ({
          uid: `-${index}`,
          name: attachment.name,
          status: 'done',
          url: attachment.url
        }));
        setFileList(files);
      }
    } else {
      form.resetFields();
      setFileList([]);
      setSelectedCategory('general');
    }
  }, [news, form]);

  const handleSubmit = async (values: any) => {
    if (!firestoreUser) {
      message.error(t('common:notAuthenticated'));
      return;
    }



    // Additional client-side validation
    if (!values.title?.trim()) {
      message.error('News title is required');
      return;
    }
    if (!values.description?.trim()) {
      message.error('News description is required');
      return;
    }
    if (!values.category) {
      message.error('News category is required');
      return;
    }
    if (!values.status) {
      message.error('News status is required');
      return;
    }

    // Validate reference for non-general categories
    if (values.category !== 'general') {
      const hasReferenceId = values.referenceId && values.referenceId.trim();
      

      
      if (!hasReferenceId) {
        message.error(`Reference is required for ${values.category} category`);
        return;
      }
    }

    try {
      // Convert file list to FileMetadata
      const attachments: FileMetadata[] = fileList
        .filter(file => file.status === 'done')
        .map(file => ({
          id: file.uid || `file-${Date.now()}-${Math.random()}`,
          name: file.name || '',
          url: file.url || '',
          size: file.size || 0,
          type: file.type || '',
          path: file.url || '',
          uploadedAt: new Date(),
          mimeType: file.type || ''
        }));

      // Convert photo list to ImageMetadata
      const photos: any[] = photoList
        .filter(file => file.status === 'done')
        .map((file, index) => ({
          id: file.uid || `photo-${Date.now()}-${Math.random()}`,
          name: file.name || '',
          url: file.url || '',
          size: file.size || 0,
          type: file.type || '',
          path: file.url || '',
          isPrimary: index === 0, // First photo is primary
          uploadedAt: new Date()
        }));

      // Create reference data from form values
      let finalReference: NewsReference | undefined;
      if (values.category !== 'general' && values.referenceId && values.referenceName && values.referenceType) {
        finalReference = {
          id: values.referenceId,
          name: values.referenceName,
          type: values.referenceType as Exclude<NewsCategory, 'general'>
        };
      }

      const newsData: Omit<News, 'id'> = {
        title: values.title.trim(),
        description: values.description.trim(),
        category: values.category,
        reference: finalReference,
        status: values.status,
        author: {
          id: firestoreUser.id,
          name: firestoreUser.displayName || firestoreUser.email || 'Unknown',
          type: 'employee'
        },
        publishDate: new Date(), // Auto-set to current date
        expiryDate: values.expiryDate?.toDate() || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        photos: photos.length > 0 ? photos : undefined,
        workspace: {
          id: firestoreUser.workspace!.id,
          name: firestoreUser.workspace!.name,
          slug: firestoreUser.workspace!.slug
        }
      };

      if (news) {
        updateNewsMutation.mutate({ id: news.id, data: newsData });
      } else {
        addNewsMutation.mutate(newsData);
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      message.error(`Form submission failed: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setPhotoList([]);
    setSelectedCategory('general');
    onCancel();
  };

  const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
  };

  const handlePhotoChange = ({ fileList: newPhotoList }: { fileList: UploadFile[] }) => {
    setPhotoList(newPhotoList);
  };

  const handleCategoryChange = (category: NewsCategory) => {
    setSelectedCategory(category);
    form.setFieldValue('referenceId', undefined);
  };

  const handleReferenceChange = (reference: { id: string; name: string } | null) => {
    if (reference) {
      // Set hidden form fields for reference data
      form.setFieldValue('referenceId', reference.id);
      form.setFieldValue('referenceName', reference.name);
      form.setFieldValue('referenceType', selectedCategory);
    } else {
      // Clear hidden form fields
      form.setFieldValue('referenceId', undefined);
      form.setFieldValue('referenceName', undefined);
      form.setFieldValue('referenceType', undefined);
    }
  };

  const uploadProps = {
    beforeUpload: () => false, // Prevent auto upload
    fileList,
    onChange: handleFileChange,
    multiple: true
  };

  const photoUploadProps = {
    beforeUpload: () => false, // Prevent auto upload
    fileList: photoList,
    onChange: handlePhotoChange,
    multiple: true,
    accept: 'image/*' // Only accept images
  };

  const renderReferenceSelector = () => {
    if (selectedCategory === 'general') {
      return null;
    }

    switch (selectedCategory) {
      case 'alert':
        return <AlertSelector onAlertDataChange={handleReferenceChange} />;
      case 'event':
        return <EventSelector onEventDataChange={handleReferenceChange} />;
      case 'guideline':
        return <GuidelineSelector onGuidelineDataChange={handleReferenceChange} />;
      case 'incident':
        return <IncidentSelector onIncidentDataChange={handleReferenceChange} />;
      case 'lostAndFound':
        return <LostAndFoundSelector onLostAndFoundDataChange={handleReferenceChange} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          {news ? t('news:editNews') : t('news:addNews')}
        </Title>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: 'draft',
          category: 'general'
        }}
      >
        {/* First row: category and status */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="category"
              label={t('news:category')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select 
                placeholder={t('news:selectCategory')}
                onChange={handleCategoryChange}
              >
                {NEWS_CATEGORIES.map(category => (
                  <Select.Option key={category.value} value={category.value}>
                    {category.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('news:status')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('news:selectStatus')}>
                {NEWS_STATUSES.map(status => (
                  <Select.Option key={status.value} value={status.value}>
                    {status.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Second row: reference selector (if any) */}
        {renderReferenceSelector() && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="referenceId"
                label={t('news:reference')}
                rules={[
                  {
                    required: selectedCategory !== 'general',
                    message: t('news:referenceRequired')
                  }
                ]}
              >
                {renderReferenceSelector()}
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Hidden fields for reference data */}
        <Form.Item name="referenceName" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="referenceType" hidden>
          <Input />
        </Form.Item>

        {/* Third row: news title (full width) */}
        <Form.Item
          name="title"
          label={t('news:title')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <Input placeholder={t('news:titlePlaceholder')} />
        </Form.Item>

        {/* Fourth row: description (full width) */}
        <Form.Item
          name="description"
          label={t('news:description')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <TextArea 
            rows={6} 
            placeholder={t('news:descriptionPlaceholder')} 
          />
        </Form.Item>

        {/* Fifth row: expiry date */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="expiryDate"
              label={t('news:expiryDate')}
            >
              <DatePicker 
                showTime 
                style={{ width: '100%' }} 
                placeholder={t('news:selectExpiryDate')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Sixth row: photos and attachments */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('news:photos', 'Photos')}
            >
              <Upload {...photoUploadProps}>
                <Button icon={<UploadOutlined />}>
                  {t('common:actions.upload')} Photos
                </Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('news:attachments')}
            >
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>
                  {t('common:actions.upload')} Files
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>
              {t('common:actions.cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={addNewsMutation.isPending || updateNewsMutation.isPending}
            >
              {news ? t('common:actions.update') : t('common:actions.add')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
} 